import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const { activeTenant } = useTenant();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", password: "", role: "employee" });

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenant?.id]);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/auth/register", { ...draft, tenant_id: activeTenant?.id });
    setOpen(false);
    setDraft({ name: "", email: "", password: "", role: "employee" });
    load();
  };

  const isSuper = ["platform_owner", "super_admin", "platform_admin"].includes(user?.role);

  return (
    <div className="p-8 max-w-5xl" data-testid="users-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="n-label">Members · {activeTenant?.name || "All tenants"}</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Users & roles</h1>
        </div>
        <button onClick={() => setOpen(true)} className="n-btn flex items-center gap-2" data-testid="invite-user-button">
          <Plus size={14} /> Invite user
        </button>
      </div>

      <div className="n-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] border-b border-[var(--n-border)]">
            <tr className="text-left">
              {["Name", "Email", "Role", "Tenant", "Created"].map((h) => (
                <th key={h} className="n-label py-2 px-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[var(--n-border)]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-cab font-medium">{u.name}</td>
                <td className="px-4 py-3 text-[var(--n-text-sec)]">{u.email}</td>
                <td className="px-4 py-3"><span className="n-chip">{u.role}</span></td>
                <td className="px-4 py-3 text-xs font-mono-plex text-[var(--n-text-muted)]">
                  {u.tenant_id ? u.tenant_id.slice(0, 8) + "…" : "—"}
                </td>
                <td className="px-4 py-3 text-xs font-mono-plex text-[var(--n-text-muted)]">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="n-card w-full max-w-md p-6" data-testid="invite-user-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-cab font-black text-xl">Invite user</div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            {["name", "email", "password"].map((k) => (
              <div key={k} className="mb-3">
                <label className="n-label block mb-1">{k}</label>
                <input
                  type={k === "password" ? "password" : k === "email" ? "email" : "text"}
                  className="n-input"
                  value={draft[k]}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  required
                  minLength={k === "password" ? 8 : undefined}
                  data-testid={`invite-${k}`}
                />
              </div>
            ))}
            <div className="mb-4">
              <label className="n-label block mb-1">role</label>
              <select className="n-input" value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} data-testid="invite-role">
                <option value="employee">employee</option>
                <option value="manager">manager</option>
                <option value="business_owner">business owner</option>
                <option value="read_only">read only</option>
                {isSuper && <option value="platform_admin">platform admin</option>}
                {isSuper && <option value="platform_owner">platform owner</option>}
              </select>
            </div>
            <button type="submit" className="n-btn w-full" data-testid="invite-submit">INVITE</button>
          </form>
        </div>
      )}
    </div>
  );
}
